import React from "react";
import {
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Tooltip,
} from "recharts";
import { TID } from "@/lib/testIds";
import { SectionTitle } from "@/components/FinancialCharts";
import { Gauge } from "lucide-react";

const DIMS = [
  { key: "growth", label: "成長性" },
  { key: "profitability", label: "獲利能力" },
  { key: "safety", label: "財務安全性" },
  { key: "moat", label: "競爭優勢" },
  { key: "valuation", label: "估值合理性" },
  { key: "sentiment", label: "新聞情緒" },
  { key: "long_term", label: "長期潛力" },
];

export default function AIScoreRadar({ score }) {
  const data = DIMS.map((d) => ({ dim: d.label, value: score[d.key] ?? 0 }));

  const overall = score.overall ?? 0;
  const color = overall >= 80 ? "#10B981" : overall >= 60 ? "#3B82F6" : "#F59E0B";

  return (
    <section>
      <SectionTitle icon={Gauge} title="AI 綜合評分" subtitle="AI Score · 7 個面向" />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div
          className="lg:col-span-5 card-tactical p-5 sm:p-6 flex flex-col items-center justify-center"
          data-testid={TID.aiOverallScore}
        >
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
            整體評分
          </div>
          <div
            className="metric-value font-black leading-none"
            style={{ fontSize: 96, color, lineHeight: 1 }}
          >
            {overall}
          </div>
          <div className="mt-2 text-sm text-zinc-500 font-mono">/ 100</div>
          <div className="mt-5 text-center text-xs sm:text-sm text-zinc-300 max-w-sm leading-relaxed">
            {score.verdict}
          </div>

          <div className="mt-5 grid grid-cols-3 sm:grid-cols-4 gap-2 w-full">
            {DIMS.map((d) => (
              <div
                key={d.key}
                className="rounded-md border border-zinc-800 bg-zinc-900/60 px-2 py-1.5 text-center"
              >
                <div className="text-[10px] text-zinc-500">{d.label}</div>
                <div className="metric-value text-sm font-bold text-zinc-100">
                  {score[d.key]}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7 card-tactical p-4 sm:p-5" data-testid={TID.aiScoreRadar}>
          <div className="text-[11px] uppercase tracking-widest font-semibold text-zinc-500 mb-2">
            AI 面向雷達 · Radar
          </div>
          <div style={{ width: "100%", height: 360 }}>
            <ResponsiveContainer>
              <RadarChart data={data} outerRadius="72%">
                <PolarGrid stroke="#27272a" />
                <PolarAngleAxis
                  dataKey="dim"
                  tick={{ fill: "#a1a1aa", fontSize: 12, fontFamily: "Chivo" }}
                />
                <PolarRadiusAxis
                  angle={90}
                  domain={[0, 100]}
                  tick={{ fill: "#3f3f46", fontSize: 10, fontFamily: "JetBrains Mono" }}
                  stroke="#27272a"
                />
                <Radar
                  dataKey="value"
                  stroke="#3B82F6"
                  strokeWidth={2}
                  fill="#3B82F6"
                  fillOpacity={0.28}
                />
                <Tooltip
                  contentStyle={{
                    background: "#18181B",
                    border: "1px solid #3f3f46",
                    borderRadius: 6,
                    fontFamily: "JetBrains Mono",
                    fontSize: 12,
                  }}
                  labelStyle={{ color: "#a1a1aa" }}
                  itemStyle={{ color: "#fff" }}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </section>
  );
}
